/**
 * useConsumptionCertificateConfig — the module's ops hook (the ONLY store write path).
 *
 * MODULE-CONTRACT §2. The module's state IS its flow DTO at
 * `productConfigs["${cartItemId}|consumption_certificate"]`; this hook wraps it with
 * a typed, field-merged read plus two writers:
 *  - `patch(partial)`      — owned-only-field writes (basement, ventilation, reason…).
 *  - `applyDerived(fn)`    — the cross-derived write path (C1). `fn` receives the
 *    LIVE (field-merged) DTO read fresh from `getState()` — never a mount-time
 *    snapshot — and returns {owned field + declared cross-derived siblings}. This is
 *    where the codec's `patch*` setters are applied field-locally.
 *
 * Both writers `setConfig` (JSON-eq idempotency-guarded) + `markConfigDirty`
 * (un-confirm on edit). No imperative handle, no serialize/hydrate —
 * `serializeDraft`/`hydrateFromDraft` already round-trip `productConfigs`.
 */

import type { CartItemId } from 'models/purchaseFlowRevamp'

import { useFlowConfig } from '../useFlowConfig'
import {
  CONSUMPTION_CERTIFICATE_DEFAULT,
  consumptionCertificateIsComplete,
  type ConsumptionCertificateConfigDTO
} from './consumptionCertificate.dto'

const FLOW_ID = 'consumption_certificate' as const

export interface UseConsumptionCertificateConfig {
  /** Field-complete DTO — safe to read every field, even against a partial or
   *  absent persisted entry (§2 partial-DTO invariant). */
  value: ConsumptionCertificateConfigDTO
  /** Merge-and-write an owned-only partial DTO. */
  patch: (partial: Partial<ConsumptionCertificateConfigDTO>) => void
  /** Cross-derived write path (C1): recomputes {owned + siblings} from the LIVE DTO. */
  applyDerived: (fn: (current: ConsumptionCertificateConfigDTO) => Partial<ConsumptionCertificateConfigDTO>) => void
  isComplete: boolean
}

export function useConsumptionCertificateConfig(cartItemId: CartItemId): UseConsumptionCertificateConfig {
  const { value, patch, update, isComplete } = useFlowConfig(
    cartItemId, FLOW_ID, CONSUMPTION_CERTIFICATE_DEFAULT, consumptionCertificateIsComplete
  )
  return { value, patch, applyDerived: update, isComplete }
}
